import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { cx } from '@compiled/react';
import parseISO from 'date-fns/parseISO';
import { defineMessages, FormattedDate, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useValidateRequiredField } from '../hooks/useValidateRequiredField';

import { ValidationPopup } from './ValidationPopup';

const messages = defineMessages({
	none: {
		id: 'jira.jira-platform-fields.base-date-field.none',
		defaultMessage: 'None',
		description: 'Value when no date is selected',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
		// The InlineEdit component has a default 8px margin top, so we are negating this here
		marginTop: token('space.negative.100'),
	},
	readView: {
		width: '100%',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
	editableReadView: {
		cursor: 'pointer',
		height: '36px', // 40px - 2 * 2px border
	},
});

const parseDate = (dateString: string | null | undefined): Date | null => {
	if (dateString) {
		const date = parseISO(dateString);
		if (date.toString() !== 'Invalid Date') {
			return date;
		}
	}
	return null;
};

const ReadView = ({
	dateString,
	includeTime,
	isEditable,
}: {
	dateString: string | null | undefined;
	includeTime: boolean;
	isEditable: boolean;
}) => {
	const { formatMessage } = useIntl();
	const date = parseDate(dateString);

	return (
		<Box xcss={cx(styles.readView, isEditable && styles.editableReadView)}>
			<Text align="start" color={date ? 'color.text' : 'color.text.subtle'} maxLines={1}>
				{date ? (
					<FormattedDate
						value={date}
						year="numeric"
						month="short"
						day="2-digit"
						{...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {})}
					/>
				) : (
					formatMessage(messages.none)
				)}
			</Text>
		</Box>
	);
};

export type BaseDateFieldProps = {
	currentValue: string | null | undefined;
	defaultValue: string | null | undefined;
	fieldName?: string;
	includeTime?: boolean;
	isEditable: boolean;
	isRequired: boolean;
	labelText: string;
	onChange?: (value: string | null) => void;
	renderPicker: (args: {
		autoFocus: boolean;
		errorId: string;
		isInvalid: boolean;
		isRequired: boolean;
		locale: string;
		onChange: (newValue: string) => void;
		placeholder: string;
		value: string;
	}) => JSX.Element;
};

export const BaseDateField: React.MemoExoticComponent<
	({
		fieldName,
		labelText,
		isEditable,
		isRequired,
		currentValue,
		defaultValue,
		includeTime,
		onChange,
		renderPicker,
	}: BaseDateFieldProps) => JSX.Element
> = memo(
	({
		fieldName,
		labelText,
		isEditable,
		isRequired,
		currentValue,
		defaultValue,
		includeTime = false,
		onChange,
		renderPicker,
	}: BaseDateFieldProps): JSX.Element => {
		const { locale, formatDate } = useIntl();

		const validate = useValidateRequiredField({
			isRequired,
			isEditable,
			fieldName: fieldName || labelText,
			fieldTypeForAnalytics: 'DateField',
		});

		const placeholder = useMemo(() => formatDate(new Date()), [formatDate]);

		const handleOnConfirm = useCallback(
			(newValue?: string) => {
				onChange?.(newValue ?? null);
			},
			[onChange],
		);

		const readView = useMemo(() => {
			return (
				<ReadView dateString={currentValue} includeTime={includeTime} isEditable={isEditable} />
			);
		}, [currentValue, includeTime, isEditable]);

		// Allows us to make sure we only fall back to default value once, on initial render
		const isFirstRenderRef = useRef(true);
		useEffect(() => {
			// If the passed down value is empty and a default value is available, tell the upstream
			if (!currentValue && defaultValue != null && isFirstRenderRef.current) {
				onChange?.(defaultValue);
			}
			isFirstRenderRef.current = false;
		}, [currentValue, defaultValue, onChange]);

		if (!isEditable) {
			return readView;
		}

		return (
			<Box xcss={styles.container}>
				<InlineEdit
					defaultValue={currentValue || ''}
					readView={() => readView}
					editView={({ errorMessage, isInvalid, ...fieldProps }) => (
						<ValidationPopup
							errorMessage={errorMessage}
							showInvalidPopup={Boolean(isInvalid && errorMessage)}
							trigger={({ ref, errorId }) => (
								<Box ref={ref}>
									{renderPicker({
										...fieldProps,
										errorId,
										isInvalid,
										isRequired,
										placeholder,
										locale,
										autoFocus: true,
									})}
								</Box>
							)}
						/>
					)}
					validate={validate}
					onConfirm={handleOnConfirm}
					hideActionButtons
					readViewFitContainerWidth
					editButtonLabel={currentValue ?? undefined}
				/>
			</Box>
		);
	},
);
